import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import Layout from "@/components/Layout";
import PortalLayout from "@/components/PortalLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Dispatch from "@/pages/Dispatch";
import FocusCabin from "@/pages/FocusCabin";
import LiveMap from "@/pages/LiveMap";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import ServiceNew from "@/pages/ServiceNew";
import Quoter from "@/pages/Quoter";
import Quotes from "@/pages/Quotes";
import FRAP from "@/pages/FRAP";
import Profitability from "@/pages/Profitability";
import SLA from "@/pages/SLA";
import Expirations from "@/pages/Expirations";
import Attendance from "@/pages/Attendance";
import Integrations from "@/pages/Integrations";
import HospitalContacts from "@/pages/HospitalContacts";
import Agreements from "@/pages/Agreements";
import PriceLists from "@/pages/PriceLists";
import Inventory from "@/pages/Inventory";
import AgreementDetail from "@/pages/AgreementDetail";
import Clients from "@/pages/Clients";
import Units from "@/pages/Units";
import Personnel from "@/pages/Personnel";
import Payroll from "@/pages/Payroll";
import Maintenance from "@/pages/Maintenance";
import Receivable from "@/pages/Receivable";
import Payable from "@/pages/Payable";
import Aging from "@/pages/Aging";
import ClientStatement from "@/pages/ClientStatement";
import Incidents from "@/pages/Incidents";
import Reports from "@/pages/Reports";
import CrewMobile from "@/pages/CrewMobile";
import Settings from "@/pages/Settings";
import Audit from "@/pages/Audit";
import PortalUsers from "@/pages/PortalUsers";
import NotificationSettings from "@/pages/NotificationSettings";
import Users from "@/pages/Users";
import Prospects from "@/pages/Prospects";
import PortalHome from "@/pages/portal/PortalHome";
import PortalRequest from "@/pages/portal/PortalRequest";
import PortalServices from "@/pages/portal/PortalServices";
import PortalServiceDetail from "@/pages/portal/PortalServiceDetail";
import PortalInvoices from "@/pages/portal/PortalInvoices";
import NPSRespond from "@/pages/NPSRespond";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import { Toaster } from "sonner";
import "@/App.css";

import { roleHome } from "@/lib/rolePolicy";

function RoleRedirect() {
    const { user } = useAuth();
    return <Navigate to={roleHome(user)} replace />;
}

function AppRoutes() {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="h-screen flex items-center justify-center bg-slate-100">
                <div className="text-slate-500 text-sm font-mono">Cargando FindMed Dispatch Control...</div>
            </div>
        );
    }
    const loginRedirect = roleHome(user);
    return (
        <Routes>
            <Route path="/login" element={user ? <Navigate to={loginRedirect} replace /> : <Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />

            {/* Encuesta NPS pública (sin login) */}
            <Route path="/nps/:token" element={<NPSRespond />} />

            {/* Modo Concentración Cabina · full screen, sin Layout */}
            <Route path="/focus" element={<ProtectedRoute><FocusCabin /></ProtectedRoute>} />

            {/* Portal corporativo */}
            <Route element={<ProtectedRoute><PortalLayout /></ProtectedRoute>}>
                <Route path="/portal" element={<PortalHome />} />
                <Route path="/portal/request" element={<PortalRequest />} />
                <Route path="/portal/services" element={<PortalServices />} />
                <Route path="/portal/services/:id" element={<PortalServiceDetail />} />
                <Route path="/portal/invoices" element={<PortalInvoices />} />
                <Route path="/portal/sla" element={<SLA scope="portal" />} />
            </Route>

            {/* Sistema interno */}
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                <Route path="/" element={<RoleRedirect />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/dispatch" element={<Dispatch />} />
                <Route path="/map" element={<LiveMap />} />
                <Route path="/services" element={<Services />} />
                <Route path="/services/new" element={<ServiceNew />} />
                <Route path="/services/:id" element={<ServiceDetail />} />
                <Route path="/services/:id/frap" element={<FRAP />} />
                <Route path="/quoter" element={<Quoter />} />
                <Route path="/quotes" element={<Quotes />} />
                <Route path="/clients" element={<Clients />} />
                <Route path="/agreements" element={<Agreements />} />
                <Route path="/agreements/:id" element={<AgreementDetail />} />
                <Route path="/price-lists" element={<PriceLists />} />
                <Route path="/inventory" element={<Inventory />} />
                <Route path="/profitability" element={<Profitability />} />
                <Route path="/sla" element={<SLA />} />
                <Route path="/expirations" element={<Expirations />} />
                <Route path="/attendance" element={<Attendance />} />
                <Route path="/integrations" element={<Integrations />} />
                <Route path="/hospital-contacts" element={<HospitalContacts />} />
                <Route path="/units" element={<Units />} />
                <Route path="/personnel" element={<Personnel />} />
                <Route path="/payroll" element={<Payroll />} />
                <Route path="/maintenance" element={<Maintenance />} />
                <Route path="/receivable" element={<Receivable />} />
                <Route path="/payable" element={<Payable />} />
                <Route path="/aging" element={<Aging />} />
                <Route path="/statement/:client_id" element={<ClientStatement />} />
                <Route path="/incidents" element={<Incidents />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/crew" element={<CrewMobile />} />
                <Route path="/audit" element={<Audit />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/portal-users" element={<PortalUsers />} />
                <Route path="/notifications" element={<NotificationSettings />} />
                <Route path="/users" element={<Users />} />
                <Route path="/prospects" element={<Prospects />} />
            </Route>

            <Route path="*" element={<RoleRedirect />} />
        </Routes>
    );
}

export default function App() {
    return (
        <ErrorBoundary>
            <AuthProvider>
                <BrowserRouter>
                    <AppRoutes />
                    <Toaster position="top-right" richColors expand={false} duration={3500} closeButton />
                </BrowserRouter>
            </AuthProvider>
        </ErrorBoundary>
    );
}
